/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import toast from "react-hot-toast";
import { FaFolderOpen } from "react-icons/fa6";
import { BsThreeDotsVertical, BsUpload, BsFillFolderFill, } from "react-icons/bs";
import { IoChevronForward, IoSearch } from "react-icons/io5";
import { MdDeleteForever } from "react-icons/md";
import { LuFolderPlus } from "react-icons/lu";
import { FileIcon, StatusBadge } from "./UploadBadges";
import { resolveMediaUrl } from "./PreviewUploadModal";
import { countTotalItems } from "./FolderTreeNode";
import { getExternalFilesFromDrop, isInternalItemDrag } from "./dnsDragMove";
import sweetAlert from "../Common/sweetAlert";

const FileDetailPanel = React.memo(
  ({
    selectedOrg,
    selectedFolder,
    onSelectFolder,
    onSelectOrg,
    dynamicUploads,
    isUploadsLoading = false,
    folderPermissions,
    user,
    breadcrumb,
    isSorted,
    folderNodeMap,
    uploadsCountMap,
    /* mutations passed as props so FileDetailPanel can call them directly */
    deleteUploadMutation,
    updateUploadLockMutation,
    updateUploadStatusMutation,
    updateUploadTagsMutation,
    uploadFilesMutation,
    /* cross-panel callbacks */
    onPreviewFile,
    onOpenRename,
    onOpenTags,
    onStartMove,
    /* drag-move (controlled by parent) */
    draggingMoveItem,
    moveDropTargetId,
    onMoveDragStart,
    onMoveDragEnd,
    onMoveDragOverFolder,
    onMoveDropOnFolder,
    onAddFolder,
  }) => {
    // ── Local state ───────────────────────────────────────────────
    const [fileSearch, setFileSearch] = useState("");
    const [fileActionId, setFileActionId] = useState(null);
    const [fileActionPos, setFileActionPos] = useState({ top: 0, right: 0, bottom: "auto" });
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [selectedUploadIds, setSelectedUploadIds] = useState([]);
    const [isUploadDragOver, setIsUploadDragOver] = useState(false);
    // ── Local refs ────────────────────────────────────────────────
    const fileActionRef = useRef(null);
    const uploadInputRef = useRef(null);
    const uploadDragCounterRef = useRef(0);

    // ── Reset selection when folder / page / search changes ───────
    useEffect(() => {
      setSelectedUploadIds([]);
    }, [selectedFolder, page, fileSearch]);

    // ── Reset page when org or folder changes ─────────────────────
    const selectedFolderId =
      selectedFolder?.folderDNSID ?? selectedFolder?.folderID;
    useEffect(() => {
      setPage(1);
      setFileSearch("");
    }, [selectedOrg?.organizationsDNSId, selectedFolderId]);

    // ── Close file action menu on scroll ─────────────────────────
    useEffect(() => {
      const handleScroll = () => {
        if (fileActionId) setFileActionId(null);
      };
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }, [fileActionId]);

    // ── Close file action menu on outside click ───────────────────
    useEffect(() => {
      const close = (e) => {
        if (e.target.closest("button")) return;
        if (
          fileActionRef.current &&
          !fileActionRef.current.contains(e.target)
        ) {
          setFileActionId(null);
        }
      };
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }, []);



    // ── Derived file data ─────────────────────────────────────────
    const rawFiles =
      dynamicUploads.length > 0
        ? dynamicUploads
        : selectedFolder?.files ?? [];
    const filteredFiles = rawFiles.filter((f) =>
      (f.fileName || "").toLowerCase().includes(fileSearch.toLowerCase())
    );
    const sortedFiles = isSorted
      ? [...filteredFiles].sort((a, b) =>
        (a.fileName || "").toLowerCase().localeCompare(
          (b.fileName || "").toLowerCase()
        )
      )
      : filteredFiles;

    const totalItems = sortedFiles.length;
    const paginatedFiles = sortedFiles.slice(
      (page - 1) * perPage,
      page * perPage
    );
    const totalPages = Math.ceil(totalItems / perPage);

    // ── Upload helpers ────────────────────────────────────────────
    const canUploadFiles = Boolean(
      selectedOrg && folderPermissions.isEdit && !uploadFilesMutation.isPending
    );

    const uploadFilesFromList = async (fileList) => {
      if (!fileList || fileList.length === 0) return;
      if (!selectedOrg) {
        toast.error("Please select an organization first.");
        return;
      }
      if (!folderPermissions.isEdit) {
        toast.error("You don't have permission to upload here.");
        return;
      }
      const parentId = selectedFolder?.folderDNSID ?? 0;
      const toastId = toast.loading(
        `Uploading ${fileList.length} file${fileList.length > 1 ? "s" : ""}...`,
        { duration: Infinity }
      );
      try {
        await uploadFilesMutation.mutateAsync({ parentId, files: fileList });
        toast.success(
          `${fileList.length} file${fileList.length > 1 ? "s" : ""} uploaded successfully!`,
          { id: toastId }
        );
        toast.remove();
      } catch {
        toast.error("Upload failed. Please try again.", { id: toastId });
      }
    };

    const handleUpload = async (e) => {
      await uploadFilesFromList(e.target.files);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    };

    const handleUploadDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInternalItemDrag(e.dataTransfer)) return;
      if (!canUploadFiles) return;
      uploadDragCounterRef.current += 1;
      setIsUploadDragOver(true);
    };

    const handleUploadDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInternalItemDrag(e.dataTransfer)) return;
      if (!canUploadFiles) return;
      e.dataTransfer.dropEffect = "copy";
    };

    const handleUploadDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInternalItemDrag(e.dataTransfer)) return;
      if (!canUploadFiles) return;
      uploadDragCounterRef.current -= 1;
      if (uploadDragCounterRef.current <= 0) {
        uploadDragCounterRef.current = 0;
        setIsUploadDragOver(false);
      }
    };

    const handleUploadDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      uploadDragCounterRef.current = 0;
      setIsUploadDragOver(false);
      if (isInternalItemDrag(e.dataTransfer)) return;
      if (!canUploadFiles) return;
      const files = getExternalFilesFromDrop(e.dataTransfer);
      if (files.length === 0) {
        toast.error(
          "Only files can be uploaded here. Drag folders in the tree to move them."
        );
        return;
      }
      await uploadFilesFromList(files);
    };

    // ── File action handlers ──────────────────────────────────────
    const handleFileOrFolderClick = (file) => {
      if (file.fileType === "Folder") {
        const folderNode = {
          folderDNSID: file.uploadID,
          folderName: file.fileName,
          parentId: file.parentId,
        };
        onSelectFolder(folderNode);
      } else {
        onPreviewFile(file);
      }
    };

    const handleDeleteUpload = (id = null, name = "", type = "Multiple") => {
      const isBulk = id === null || typeof id === "object";
      const idsToDelete = isBulk ? selectedUploadIds : [id];
      if (idsToDelete.length === 0) return;

      const title = isBulk ? "Delete Selected" : `Delete ${type}`;
      const text = isBulk
        ? `Are you sure you want to delete ${idsToDelete.length} item(s)? This cannot be undone.`
        : `Are you sure you want to delete "${name}"? This cannot be undone.`;

      sweetAlert.confirm(title, text, "delete").then(async (result) => {
        if (result.isConfirmed) {
          try {
            const payload = [];
            if (isBulk) {
              const folders = [];
              const files = [];
              idsToDelete.forEach((uid) => {
                const fileObj = paginatedFiles.find((f) => f.uploadID === uid);
                if (fileObj) {
                  if (fileObj.fileType === "Folder") folders.push(uid);
                  else files.push(uid);
                }
              });
              if (folders.length > 0)
                payload.push({
                  Type: "Folder",
                  UploadIDs: folders.join(","),
                  isDeleted: true,
                });
              if (files.length > 0)
                payload.push({
                  Type: "File",
                  UploadIDs: files.join(","),
                  isDeleted: true,
                });
            } else {
              payload.push({
                Type: type,
                UploadIDs: String(id),
                isDeleted: true,
              });
            }
            await deleteUploadMutation.mutateAsync({ payload });
            toast.success(
              isBulk
                ? `${idsToDelete.length} item(s) deleted successfully!`
                : `"${name}" deleted successfully!`
            );
            if (isBulk) setSelectedUploadIds([]);
          } catch {
            toast.error(
              isBulk
                ? "Failed to delete selected items."
                : `Failed to delete ${type.toLowerCase()}.`
            );
          }
        }
      });
    };

    const handleToggleUploadLock = async (uploadId, currentLocked) => {
      const isLocked = !currentLocked;
      try {
        await updateUploadLockMutation.mutateAsync({ uploadId, isLocked });
        toast.success(
          isLocked ? "Locked (Checked-out) successfully!" : "Unlocked successfully!"
        );
      } catch {
        toast.error(isLocked ? "Failed to lock." : "Failed to unlock.");
      }
    };

    const handleToggleUploadStatus = async (uploadId, currentStatus) => {
      const status = currentStatus === "Published" ? "Draft" : "Published";
      try {
        await updateUploadStatusMutation.mutateAsync({ uploadId, status });
        toast.success(`Status updated to ${status} successfully!`);
      } catch {
        toast.error("Failed to update status.");
      }
    };

    const handleBulkDownload = async () => {
      if (selectedUploadIds.length === 0) return;
      
      const filesToDownload = paginatedFiles.filter(
        (f) => selectedUploadIds.includes(f.uploadID) && f.fileType !== "Folder"
      );
      
      if (filesToDownload.length === 0) {
        toast.error("Please select files to download. Folders cannot be downloaded.");
        return;
      }

      const toastId = toast.loading(
        `Downloading ${filesToDownload.length} file${filesToDownload.length > 1 ? "s" : ""}...`
      );

      let successCount = 0;
      let failedCount = 0;

      for (const file of filesToDownload) {
        try {
          const url = resolveMediaUrl(file);
          if (!url) {
            failedCount++;
            continue;
          }

          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = file.fileName || "download";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
          successCount++;
          
          // Add small delay between downloads to avoid browser blocking
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch {
          failedCount++;
        }
      }

      toast.remove(toastId);
      
      if (successCount > 0) {
        toast.success(
          `${successCount} file${successCount > 1 ? "s" : ""} downloaded successfully!`
        );
      }
      
      if (failedCount > 0) {
        toast.error(
          `${failedCount} file${failedCount > 1 ? "s" : ""} failed to download.`
        );
      }
      
      setSelectedUploadIds([]);
    };

    // ── Render ────────────────────────────────────────────────────
    return (
      <div className="flex-1 min-w-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col overflow-hidden">
        {/* ── Breadcrumb + Toolbar row ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3 flex-wrap">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-sm text-slate-500 font-medium flex-wrap">
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <IoChevronForward
                    size={14}
                    className="text-slate-300 shrink-0"
                  />
                )}
                <span
                  className={`${i === breadcrumb.length - 1
                    ? "text-[15px] text-indigo-700 font-bold"
                    : "hover:text-black cursor-pointer transition-colors"
                    }`}
                  onClick={() => {
                    if (i === breadcrumb.length - 1) return;
                    if (crumb.node === null) {
                      onSelectOrg(selectedOrg);
                    } else {
                      onSelectFolder(crumb.node);
                    }
                  }}
                >
                  {crumb.name}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Right-side action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Hidden file input */}
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              accept="*/*"
              className="hidden"
              onChange={handleUpload}
            />
            {folderPermissions.isEdit && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="Upload"
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploadFilesMutation.isPending || !selectedOrg}
                className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <BsUpload size={15} />
              </button>
            )}
            {folderPermissions.isEdit && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="New Folder"
                onClick={onAddFolder}
                className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
              >
                <LuFolderPlus size={15} />
              </button>
            )}
            {selectedUploadIds.length > 0 && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="Download"
                onClick={() => handleBulkDownload()}
                className="flex items-center p-2 text-xs font-semibold bg-green-600 text-white rounded-full transition-colors hover:bg-green-700"
              >
                <BsUpload size={15} className="rotate-180" />
              </button>
            )}
            {folderPermissions.isDelete && selectedUploadIds.length > 0 && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="Delete"
                onClick={() => handleDeleteUpload()}
                className="flex items-center p-1.5 text-xs font-semibold bg-red text-white rounded-full transition-colors"
              >
                <MdDeleteForever size={16} />
              </button>
            )}
          </div>
        </div>

        {/* ── Folder Info Bar ── */}
        {selectedFolder?.folderName && (
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-50 bg-slate-50/40">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <BsFillFolderFill size={20} className="text-indigo-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 leading-tight">
                {selectedFolder.folderName}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {totalItems} items
                {selectedFolder?.updatedDate && (
                  <>
                    {" "}
                    · Updated{" "}
                    {new Date(selectedFolder.updatedDate).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* ── File Table + Search + Pagination ── */}
        <div
          className={`flex-1 overflow-hidden flex flex-col relative ${isUploadDragOver && canUploadFiles ? "dns-upload-drop-active" : ""
            }`}
          onDragEnter={handleUploadDragEnter}
          onDragOver={handleUploadDragOver}
          onDragLeave={handleUploadDragLeave}
          onDrop={handleUploadDrop}
        >
          {/* Drop overlay */}
          {isUploadDragOver && canUploadFiles && (
            <div className="dns-upload-drop-overlay" aria-hidden="true">
              <div className="dns-upload-drop-overlay-inner">
                <BsUpload size={28} className="text-indigo-600" />
                <p className="text-sm font-bold text-indigo-700">
                  Drop files to upload
                </p>
                <p className="text-xs text-indigo-500/90">
                  Release to add to this folder
                </p>
              </div>
            </div>
          )}

          {/* Search bar */}
          <div className="px-5 py-3 border-b border-slate-50">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 max-w-sm">
              <IoSearch size={13} className="text-slate-400 shrink-0" />
              <input
                value={fileSearch}
                onChange={(e) => {
                  setFileSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search files..."
                className="bg-transparent text-xs text-slate-700 placeholder-slate-400 flex-1 outline-none"
              />
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto scroll-inner">
            <table className="w-full min-w-max text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-slate-300">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      checked={
                        paginatedFiles.length > 0 &&
                        selectedUploadIds.length === paginatedFiles.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUploadIds(
                            paginatedFiles.map((f) => f.uploadID)
                          );
                        } else {
                          setSelectedUploadIds([]);
                        }
                      }}
                    />
                  </th>
                  {["Name", "Type", "Author", "Status", "Actions"].map(
                    (h, i) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-base font-bold text-black tracking-wide ${i === 5 ? "text-right" : ""
                          }`}
                      >
                        {h}
                        {/* {h === "Name" && (
                          <span className="ml-1 text-slate-300 font-bold">
                            ↕
                          </span>
                        )} */}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {isUploadsLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className="border-b border-slate-100/50">
                      <td className="px-4 py-4 w-10">
                        <Skeleton width={16} height={16} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <Skeleton circle width={20} height={20} />
                          <Skeleton width={180} height={16} />
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton width={60} height={16} />
                      </td>
                    </tr>
                  ))
                ) : paginatedFiles.length > 0 ? (
                  paginatedFiles.map((file) => {
                    const isLocked = file?.isLocked;
                    const showOnlyView =
                      (isLocked && user?.parentID > 0) ||
                      !folderPermissions.isEdit;
                    const isFolderRow = file?.fileType === "Folder";
                    const movePayload = {
                      id: file.uploadID,
                      name: file.fileName,
                      type: isFolderRow ? "Folder" : "File",
                    };
                    const isDraggingRow =
                      draggingMoveItem?.id === file.uploadID;
                    const isMoveDropTarget =
                      isFolderRow && moveDropTargetId === file.uploadID;

                    return (
                      <tr
                        key={file.uploadID}
                        draggable={folderPermissions.isEdit && !isLocked}
                        onDragStart={(e) => onMoveDragStart(e, movePayload)}
                        onDragEnd={onMoveDragEnd}
                        onDragOver={
                          isFolderRow
                            ? (e) =>
                              onMoveDragOverFolder(e, file.uploadID)
                            : undefined
                        }
                        onDragLeave={
                          isFolderRow
                            ? () => {
                              /* parent resets via onMoveDragOverFolder */
                            }
                            : undefined
                        }
                        onDrop={
                          isFolderRow
                            ? (e) => onMoveDropOnFolder(e, file.uploadID)
                            : undefined
                        }
                        className={`transition-colors border-b border-slate-100/50 group ${isLocked
                          ? "bg-[#fffbeb] hover:bg-[#fff5cc]"
                          : "hover:bg-slate-50/60"
                          } ${isDraggingRow ? "opacity-50" : ""} ${isMoveDropTarget ? "dns-move-drop-target-row" : ""
                          }`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-4 w-10">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            checked={selectedUploadIds.includes(file.uploadID)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUploadIds((prev) => [
                                  ...prev,
                                  file.uploadID,
                                ]);
                              } else {
                                setSelectedUploadIds((prev) =>
                                  prev.filter((id) => id !== file.uploadID)
                                );
                              }
                            }}
                          />
                        </td>

                        {/* Name */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <FileIcon type={file?.fileType} size={16} />
                            <button
                              onClick={() => handleFileOrFolderClick(file)}
                              className="text-[15px] font-semibold text-slate-900 hover:text-indigo-600 transition-colors text-left focus:outline-none flex items-center gap-1.5 min-w-0"
                            >
                              <span
                                data-tooltip-id="toolbar-tooltip"
                                data-tooltip-content={file?.fileName}
                                className="whitespace-nowrap"
                              >
                                {file?.fileName}
                              </span>
                              {isLocked && (
                                <span
                                  className="text-sm shrink-0"
                                  title="Locked (Checked-out)"
                                >
                                  🔒
                                </span>
                              )}
                            </button>

                            {/* Folder item count badge */}
                            {file?.fileType === "Folder" &&
                              (() => {
                                const cachedCount = uploadsCountMap.get(
                                  file.uploadID
                                );
                                const node = folderNodeMap.get(file.uploadID);
                                const count =
                                  cachedCount ??
                                  (node ? countTotalItems(node) : 0);
                                return count > 0 ? (
                                  <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600 leading-none">
                                    {count}
                                  </span>
                                ) : null;
                              })()}

                            {/* Tags */}
                            {file?.tags && (
                              <div className="flex items-center gap-1">
                                {file.tags.split(",").map((tag, idx) => {
                                  const trimmed = tag.trim();
                                  if (!trimmed) return null;
                                  return (
                                    <span
                                      key={idx}
                                      className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100/60 shrink-0"
                                    >
                                      {trimmed}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-4 text-sm text-slate-600 font-medium">
                          {file?.fileType || "File"}
                        </td>
                        {/* Author */}
                        <td className="px-4 py-4 text-sm text-slate-600 font-medium">
                          {file?.userName || ""}
                        </td>
                        {/* Status */}
                        <td className="px-4 py-4">
                          {file.fileType === "Folder" ? (
                            "-"
                          ) : (
                            <StatusBadge status={file?.status} />
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-left">
                          <div
                            className="relative inline-block"
                            ref={
                              fileActionId === file.uploadID
                                ? fileActionRef
                                : null
                            }
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (fileActionId === file.uploadID) {
                                  setFileActionId(null);
                                } else {
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  setFileActionId(file.uploadID);
                                  const estimatedMenuHeight = 300;
                                  const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                  if (spaceBelow < estimatedMenuHeight) {
                                    setFileActionPos({
                                      top: "auto",
                                      bottom:
                                        window.innerHeight - rect.top + 4,
                                      right: window.innerWidth - rect.right,
                                    });
                                  } else {
                                    setFileActionPos({
                                      top: rect.bottom + 4,
                                      bottom: "auto",
                                      right: window.innerWidth - rect.right,
                                    });
                                  }
                                }
                              }}
                              className={`p-1.5 rounded-lg transition-all ${fileActionId === file.uploadID
                                ? "bg-indigo-50 text-indigo-600"
                                : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                }`}
                            >
                              <BsThreeDotsVertical size={14} />
                            </button>

                            {fileActionId === file.uploadID && (
                              <div
                                className="fixed z-[100] w-52 bg-white border border-slate-100 rounded-2xl shadow-2xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
                                style={{
                                  top: fileActionPos.top,
                                  bottom: fileActionPos.bottom,
                                  right: fileActionPos.right,
                                  boxShadow:
                                    "0 10px 40px -5px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.1)",
                                }}
                              >
                                {/* ── Folder actions ── */}
                                {file.fileType === "Folder" ? (
                                  <>
                                    {[
                                      {
                                        icon: (
                                          <span className="text-sm">📁</span>
                                        ),
                                        label: "Open",
                                        onClick: () => {
                                          handleFileOrFolderClick(file);
                                          setFileActionId(null);
                                        },
                                      },
                                      folderPermissions.isEdit && {
                                        icon: (
                                          <span className="text-sm">✏️</span>
                                        ),
                                        label: "Rename",
                                        onClick: () => {
                                          onOpenRename(
                                            file.uploadID,
                                            file.fileName,
                                            "Folder"
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      folderPermissions.isEdit && {
                                        icon: (
                                          <span className="text-sm">↔️</span>
                                        ),
                                        label: "Move",
                                        onClick: () => {
                                          onStartMove(
                                            {
                                              id: file.uploadID,
                                              name: file.fileName,
                                              type: "Folder",
                                            },
                                            false
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      folderPermissions.isEdit && {
                                        icon: (
                                          <span className="text-sm">📋</span>
                                        ),
                                        label: "Copy",
                                        onClick: () => {
                                          onStartMove(
                                            {
                                              id: file.uploadID,
                                              name: file.fileName,
                                              type: "Folder",
                                            },
                                            true
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                    ]
                                      .filter(Boolean)
                                      .map((a, i) => (
                                        <button
                                          key={i}
                                          onClick={a.onClick}
                                          className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                        >
                                          {a.icon}
                                          <span>{a.label}</span>
                                        </button>
                                      ))}
                                    {folderPermissions.isDelete && (
                                      <>
                                        <div className="h-px bg-slate-100 mx-3 my-1" />
                                        <button
                                          onClick={() => {
                                            handleDeleteUpload(
                                              file.uploadID,
                                              file.fileName,
                                              "Folder"
                                            );
                                            setFileActionId(null);
                                          }}
                                          className="w-full flex items-center gap-3 px-4 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                                        >
                                          <span className="text-sm">🗑️</span>
                                          <span>Delete</span>
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : showOnlyView ? (
                                  /* ── View-only file ── */
                                  <button
                                    onClick={() => {
                                      handleFileOrFolderClick(file);
                                      setFileActionId(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                  >
                                    <span className="text-base">👁️</span>
                                    <span>View</span>
                                  </button>
                                ) : (
                                  /* ── Full file actions ── */
                                  <>
                                    {[
                                      {
                                        icon: (
                                          <span className="text-base">👁️</span>
                                        ),
                                        label: "View",
                                        onClick: () => {
                                          handleFileOrFolderClick(file);
                                          setFileActionId(null);
                                        },
                                      },
                                      {
                                        icon: (
                                          <span className="text-sm">⬇️</span>
                                        ),
                                        label: "Download",
                                        onClick: async () => {
                                          const url = resolveMediaUrl(file);
                                          if (!url) return;
                                          setFileActionId(null);
                                          const toastId = toast.loading(
                                            `Downloading ${file.fileName || "file"
                                            }...`
                                          );
                                          try {
                                            const response = await fetch(url);
                                            const blob =
                                              await response.blob();
                                            const blobUrl =
                                              window.URL.createObjectURL(blob);
                                            const a =
                                              document.createElement("a");
                                            a.href = blobUrl;
                                            a.download =
                                              file.fileName || "download";
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            window.URL.revokeObjectURL(blobUrl);
                                            toast.success("Download complete!", {
                                              id: toastId,
                                            });
                                          } catch {
                                            toast.error(
                                              "Download failed. Opening in new tab...",
                                              { id: toastId }
                                            );
                                            window.open(url, "_blank");
                                          }
                                        },
                                      },
                                      {
                                        icon: (
                                          <span className="text-sm">✏️</span>
                                        ),
                                        label: "Rename",
                                        onClick: () => {
                                          onOpenRename(
                                            file.uploadID,
                                            file.fileName,
                                            "File"
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      {
                                        icon: (
                                          <span className="text-sm">↔️</span>
                                        ),
                                        label: "Move",
                                        onClick: () => {
                                          onStartMove(
                                            {
                                              id: file.uploadID,
                                              name: file.fileName,
                                              type: "File",
                                            },
                                            false
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      {
                                        icon: (
                                          <span className="text-sm">📋</span>
                                        ),
                                        label: "Copy",
                                        onClick: () => {
                                          onStartMove(
                                            {
                                              id: file.uploadID,
                                              name: file.fileName,
                                              type: "File",
                                            },
                                            true
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                    ].map((a, i) => (
                                      <button
                                        key={i}
                                        onClick={a.onClick}
                                        className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                      >
                                        {a.icon}
                                        <span>{a.label}</span>
                                      </button>
                                    ))}

                                    <div className="h-px bg-slate-100 mx-3 my-1" />

                                    {[
                                      user?.parentID === 0 && {
                                        icon:
                                          file.status === "Published" ? (
                                            <span className="text-sm">📥</span>
                                          ) : (
                                            <span className="text-sm">📤</span>
                                          ),
                                        label:
                                          file.status === "Published"
                                            ? "Unpublish"
                                            : "Publish",
                                        onClick: () => {
                                          handleToggleUploadStatus(
                                            file.uploadID,
                                            file.status
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      user?.parentID === 0 && {
                                        icon: file.isLocked ? (
                                          <span className="text-sm">🔓</span>
                                        ) : (
                                          <span className="text-sm">🔒</span>
                                        ),
                                        label: file.isLocked
                                          ? "Unlock"
                                          : "Lock (Check-out)",
                                        onClick: () => {
                                          handleToggleUploadLock(
                                            file.uploadID,
                                            file.isLocked
                                          );
                                          setFileActionId(null);
                                        },
                                      },
                                      {
                                        icon: (
                                          <span className="text-sm">🏷️</span>
                                        ),
                                        label: "Add Tags",
                                        onClick: () => {
                                          onOpenTags(file);
                                          setFileActionId(null);
                                        },
                                      },
                                    ]
                                      .filter(Boolean)
                                      .map((a, i) => (
                                        <button
                                          key={i}
                                          onClick={a.onClick}
                                          className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                        >
                                          {a.icon}
                                          <span>{a.label}</span>
                                        </button>
                                      ))}

                                    {folderPermissions.isDelete && (
                                      <>
                                        <div className="h-px bg-slate-100 mx-3 my-1" />
                                        <button
                                          onClick={() => {
                                            handleDeleteUpload(
                                              file.uploadID,
                                              file.fileName,
                                              "File"
                                            );
                                            setFileActionId(null);
                                          }}
                                          className="w-full flex items-center gap-3 px-4 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                                        >
                                          <span className="text-sm">🗑️</span>
                                          <span>Delete</span>
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-3 text-slate-400">
                        <FaFolderOpen size={32} className="text-slate-200" />
                        <p className="text-sm font-medium">No files found</p>
                        <p className="text-xs">
                          {canUploadFiles
                            ? "Drag and drop files here, use Upload, or try another search"
                            : "Try a different search or select another folder"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/30 flex-shrink-0">
            <p className="text-[11px] text-slate-500 font-medium">
              Showing {totalItems === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, totalItems)} of {totalItems} items
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs transition-colors"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors ${page === p
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs transition-colors"
              >
                ›
              </button>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="ml-1 text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

FileDetailPanel.displayName = "FileDetailPanel";
export default FileDetailPanel;
